import { ActionIcon, Loader, Popover, Select, TextInput, Textarea } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useToggle } from '@mantine/hooks';
import { Pencil, Search } from 'lucide-react';
import { FC, useEffect, useState } from 'react';

import CircularLoader from '@/components/CircularLoader.tsx';
import CustomSearchableSelect from '@/components/CustomSearchableSelect.tsx';
import DateTimePickerInput from '@/components/DateTimePickerInput.tsx';
import Icon from '@/components/Icon.tsx';
import StyledMultiSelect from '@/components/StyledMultiSelect.tsx';
import { TaskLocationsSelect } from '@/components/TaskModal/task-locations-select.tsx';
import Form from '@/components/form/Form.tsx';
import FormSection from '@/components/form/FormSection.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import usePatient from '@/hooks/usePatient.ts';
import usePvsPractitioners from '@/hooks/usePvsPractitioners.ts';
import useTaskFormOptions from '@/hooks/useTaskFormOptions.ts';
import useTasks from '@/hooks/useTasks.ts';

import { NotificationType } from '@/models/Notification.ts';
import { TaskFormValues } from '@/models/TaskFormValues.ts';
import { TaskPrefill } from '@/models/TaskPrefill.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DCTask, DCTaskStatus, DCTaskType, EnrichedTask, TaskUrgency } from '@/models/doc-cirrus/DCTask.ts';

import { task_form_schema } from '@/schemas/TaskForm.schema.ts';

import { showLoadingNotification, updateNotification } from '@/services/notificationService.ts';
import { createTaskAndEnrich, updateTaskAndEnrich } from '@/services/tasksService.ts';

import { useTaskFormStore } from '@/stores/taskFormStore.ts';
import { useTaskViewStore } from '@/stores/taskViewStore.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';
import { cn } from '@/utils/styleUtils.ts';

const executeWithNotification = async (
    operation: () => Promise<DCTask>,
    success_message: string,
    error_message: string,
    task_title: string
): Promise<DCTask | undefined> => {
    const notification_id = showLoadingNotification('Aktualisieren...', task_title);

    try {
        const result = await operation();
        updateNotification(NotificationType.SUCCESS, {
            id: notification_id,
            title: result.title,
            message: success_message,
        });
        return result;
    } catch {
        updateNotification(NotificationType.ERROR, {
            id: notification_id,
            message: error_message,
        });
        return undefined;
    }
};

const handleEmployeeChange = (form: UseFormReturnType<TaskFormValues>, value?: string | null): void => {
    form.setFieldValue('employee_id', value);

    const current_status = form.getValues().status;
    if (value && current_status !== DCTaskStatus.DONE) {
        form.setFieldValue('status', DCTaskStatus.ASSIGNED);
    } else if (!value) {
        form.setFieldValue('employee_name', '');
        if (current_status === DCTaskStatus.ASSIGNED) {
            form.setFieldValue('status', DCTaskStatus.NEW);
        }
    }
};

const handlePatientSelection = (
    form: UseFormReturnType<TaskFormValues>,
    patient_options: Array<{ value: string; label: string }>,
    value?: string
): void => {
    form.setFieldValue('patient_id', value);

    const selected_patient = patient_options.find((option) => option.value === value);
    if (selected_patient) {
        const patient_name = selected_patient.label.split(' [')[0];
        form.setFieldValue('patient_name', patient_name);
    }
};

interface Props {
    task?: EnrichedTask;
    is_loading_task?: boolean;
    prefill?: TaskPrefill;
    onSaved?: (task: EnrichedTask) => void;
}

const TaskForm: FC<Props> = ({ task, is_loading_task, prefill, onSaved }) => {
    const selectedTaskInstanceId = useTaskViewStore((state) => state.selected_task_instance_id);
    // task arrives async via SWR, so fall back to the id set at modal open for correct options on first render.
    const [selected_instance_id, setSelectedInstanceId] = useState<string | undefined>(
        task?.instance_id ?? prefill?.instance_id ?? selectedTaskInstanceId
    );
    const [is_date_popover_opened, toggleDatePopover] = useToggle([false, true]);
    const onCloseModal = useTaskViewStore((state) => state.onCloseModal);
    const { task_types, updateOrCreateTask, is_loading_open } = useTasks();
    const { instance_users_map } = usePvsPractitioners();

    const { patient } = usePatient({
        user_id: task?.patient_id ?? prefill?.patient_id,
        instance_id: task?.instance_id ?? prefill?.instance_id,
        pvs_provider: PVSProvider.DOC_CIRRUS,
    });

    const {
        practice_options,
        urgency_options,
        role_options,
        task_type_options,
        patient_options,
        instance_id_options,
        assignee_options,
        search_patient,
        onSearchPatient,
        is_loading_patient_search,
    } = useTaskFormOptions(selected_instance_id);

    useEffect(() => {
        if (!patient) return;
        // onSearchPatient is a local setter, not a parent callback; the rule false-positives on the `on` prefix.
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent, react-you-might-not-need-an-effect/no-pass-data-to-parent
        onSearchPatient(
            `${patient.personal.last_name} ${patient.personal.first_name} [PN: ${patient.patient_number}] [${dayjs(patient.personal.date_of_birth).format('DD.MM.YYYY')}]`
        );
    }, [patient]);

    const getInitialValues = (): TaskFormValues => {
        let default_task_type = task_type_options.find((type) => type.value === task?.task_type)?.value;
        if (!default_task_type && prefill?.task_type_name) {
            default_task_type = task_type_options.find((type) => type.label === prefill.task_type_name)?.value;
        }
        if (!default_task_type) {
            default_task_type = task_type_options.find((type) => type.label === 'Standard')?.value;
        }

        return {
            instance_id:
                task?.instance_id ??
                prefill?.instance_id ??
                (instance_id_options.length === 1 ? instance_id_options[0].value : ''),
            title: task?.title ?? prefill?.title ?? '',
            urgency: task?.urgency ?? TaskUrgency.LOW,
            details: task?.details ?? prefill?.details ?? '',
            alert_time: task?.alert_time ?? dayjs().toISOString(),
            is_all_day: task?.is_all_day ?? false, // default false we don't support creation of all day tasks
            type: task?.type ?? DCTaskType.UNKNOWN,
            status: task?.status ?? DCTaskStatus.NEW,
            task_type: default_task_type ?? '',
            patient_id: task?.patient_id ?? prefill?.patient_id,
            patient_name: task?.patient_name ?? prefill?.patient_name,
            employee_id: task?.employee_id,
            roles: task?.roles ?? [],
            candidates: task?.candidates ?? [],
            locations: task?.locations?.map(({ id, name }) => `${id}::${name}`) ?? prefill?.locations ?? [],
        };
    };

    const prepareTaskData = (values: TaskFormValues): EnrichedTask => ({
        id: task?.id ?? '',
        orig_type: task?.orig_type ?? DCTaskType.UNKNOWN,
        sync_state: task?.sync_state ?? '',
        schedule_id: task?.schedule_id ?? prefill?.schedule_id,
        meta: task?.meta ?? {
            created_at: '',
            updated_at: '',
            synced_at: '',
        },
        ...task,
        ...values,
        employee_id: values.employee_id ?? '',
        locations: values.locations.map((location) => {
            const [id, name] = location.split('::');
            return { id, name };
        }),
    });

    const submitForm = async (values: TaskFormValues): Promise<void> => {
        if (!values) return;

        useTaskFormStore.setState({ is_submitting: true });

        const is_editing = !!task;
        const success_message = is_editing ? 'Aufgabe aktualisiert' : 'Aufgabe erstellt';
        const error_message = is_editing
            ? 'Aufgabenaktualisierung fehlgeschlagen'
            : 'Aufgabenerstellung fehlgeschlagen';

        // Hoisted out of the try/catch: React Compiler bails on value blocks (?./||/ternary) inside try.
        const notifyTaskSaved: (task: EnrichedTask) => void = onSaved ?? (() => undefined);

        try {
            const task_data = prepareTaskData(values);
            const operation = async (): Promise<EnrichedTask> => {
                return is_editing
                    ? updateTaskAndEnrich(values.instance_id, task.id, task_data, task_types, instance_users_map)
                    : createTaskAndEnrich(values.instance_id, task_data, task_types, instance_users_map);
            };

            const updated_task = await updateOrCreateTask(async () => {
                const result = await executeWithNotification(
                    operation,
                    success_message,
                    error_message,
                    task_data.title
                );
                if (!result) throw new Error('Failed to update task');
                return result;
            });

            if (is_editing) {
                useTaskViewStore.setState((state) => ({
                    task_list: state.task_list.map((t) => (task.id === t.id ? updated_task : t)),
                }));
            } else {
                if (updated_task) {
                    useTaskViewStore.setState((state) => ({
                        task_list: [...state.task_list, updated_task],
                    }));
                }
            }

            if (updated_task) notifyTaskSaved(updated_task);

            onCloseModal();
        } catch {
            // updateOrCreateTaskOptimistically handles its own error reversion
        }

        useTaskFormStore.setState({ is_submitting: false });
    };

    const handleTaskTypeChange = (value: string, form: UseFormReturnType<TaskFormValues>): void => {
        form.setFieldValue('task_type', value ?? '');

        const current_values = form.getValues();
        const chosen_task_type = task_types.find(
            (type) => type.id === value && type.instance_id === current_values.instance_id
        );
        if (!chosen_task_type || task) return;

        const filtered_roles = chosen_task_type.roles?.filter((role) =>
            role_options.some((option) => option.value === role)
        );

        const target_date = dayjs().add(dayjs.duration(chosen_task_type.duration ?? ''));

        const updated_values: TaskFormValues = {
            ...current_values,
            title: form.isTouched('title') ? current_values.title : chosen_task_type.title,
            details: form.isTouched('details') ? current_values.details : chosen_task_type.details,
            roles: filtered_roles,
            urgency: chosen_task_type.urgency ?? TaskUrgency.NORMAL,
            candidates: chosen_task_type.candidates,
            alert_time: target_date.toISOString(),
        };

        form.setValues(updated_values);
    };

    if (is_loading_task || is_loading_open) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <CircularLoader />
            </div>
        );
    }

    return (
        <Form
            handleSubmit={submitForm}
            schema={task_form_schema}
            validateInputOnChange
            initialValues={getInitialValues()}
            className="flex w-full flex-col gap-4"
            checkIfValid={(is_valid) => useTaskFormStore.setState({ is_valid })}
        >
            {(form) => (
                <>
                    <FormSection classNames={{ wrapper: 'w-full gap-y-3' }}>
                        <TextInput
                            required
                            label="Titel"
                            value={form.values.title}
                            onChange={(e) => form.setFieldValue('title', e.target.value)}
                        />
                        <Select
                            label="Instanz"
                            required
                            data={instance_id_options}
                            disabled={instance_id_options.length === 1}
                            {...form.getInputProps('instance_id')}
                            onChange={(value: string | null) => {
                                form.setFieldValue('instance_id', value ?? '');
                                // Betriebsstätten belong to an instance, so a selection made under the
                                // previous one would otherwise be submitted against the new instance.
                                form.setFieldValue('locations', []);
                                setSelectedInstanceId(value ?? '');
                            }}
                        />
                    </FormSection>
                    <FormSection classNames={{ wrapper: 'w-full gap-y-3' }}>
                        <Select
                            label="Typ"
                            placeholder="Typ wählen"
                            data={task_type_options}
                            allowDeselect={false}
                            searchable
                            leftSection={<Search className="size-4" />}
                            {...form.getInputProps('task_type')}
                            onChange={(value: string | null) => handleTaskTypeChange(value ?? '', form)}
                            disabled={!form.values.instance_id}
                        />
                        <Select
                            label="Dringlichkeit"
                            placeholder="Dringlichkeit wählen"
                            data={urgency_options}
                            value={String(form.values.urgency)}
                            {...form.getInputProps('urgency')}
                            onChange={(value) => form.setFieldValue('urgency', value ? Number(value) : TaskUrgency.LOW)}
                            disabled={!form.values.instance_id}
                        />
                    </FormSection>
                    <Textarea
                        label="Details"
                        value={form.values.details ?? ''}
                        onChange={(e) => form.setFieldValue('details', e.target.value)}
                        className="w-full"
                        disabled={!form.values.instance_id}
                    />
                    <FormSection classNames={{ wrapper: 'w-full' }}>
                        <StyledMultiSelect
                            label="Rolle"
                            placeholder="Rolle wählen"
                            data={role_options}
                            searchable
                            leftSection={<Search className="size-4" />}
                            value={form.values.roles}
                            onChange={(value) => form.setFieldValue('roles', value ?? [])}
                            disabled={!form.values.instance_id}
                            classNames={{
                                input: 'bg-slate-100 data-[disabled=true]:border-transparent data-[disabled=true]:!bg-slate-100 data-[disabled=true]:!text-slate-400 data-[disabled=true]:!opacity-100',
                            }}
                        />
                        <StyledMultiSelect
                            label="Mitarbeiter"
                            placeholder="Mitarbeiter wählen"
                            data={assignee_options}
                            searchable
                            leftSection={<Search className="size-4" />}
                            value={form.values.candidates}
                            onChange={(value) => form.setFieldValue('candidates', value ?? [])}
                            disabled={!form.values.instance_id}
                        />
                    </FormSection>
                    {form.values?.candidates?.length === 0 && form.values?.roles?.length === 0 && (
                        <Paragraph className="-mt-2 text-sm text-red-500">
                            Bitte wählen Sie mindestens eine Rolle oder einen Mitarbeiter aus.
                        </Paragraph>
                    )}
                    <FormSection classNames={{ wrapper: 'w-full gap-y-3' }}>
                        <TaskLocationsSelect form={form} options={practice_options} isEditing={!!task} />
                    </FormSection>
                    <Popover
                        position="bottom-end"
                        opened={is_date_popover_opened}
                        onChange={toggleDatePopover}
                        disabled={!form.values.instance_id}
                        trapFocus
                        classNames={{ dropdown: 'shadow-md' }}
                    >
                        <div className={cn('transition duration-500')}>
                            <label htmlFor="date-duration" className="text-sm">
                                Zieldatum
                                <span className="ml-0.5 text-red-500">*</span>
                            </label>
                            <div
                                className={cn(
                                    'flex w-full justify-between gap-2 rounded border border-slate-300 bg-slate-100 p-2 transition duration-500',
                                    !form.getValues().instance_id && 'border-transparent text-slate-400'
                                )}
                            >
                                <Paragraph className="text-sm">
                                    <span
                                        className={cn(
                                            `relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-full after:bg-black after:content-['']`,
                                            !form.values.alert_time && 'invisible after:visible',
                                            !form.values.instance_id && 'after:bg-slate-400'
                                        )}
                                    >
                                        {dayjs(form.getValues().alert_time).format('ddd DD MMM YYYY')}
                                    </span>
                                    {` um ${
                                        form.getValues().alert_time
                                            ? dayjs(form.getValues().alert_time).format('HH:mm')
                                            : '--:--'
                                    }`}
                                </Paragraph>
                                <Popover.Target>
                                    <ActionIcon
                                        variant="transparent"
                                        aria-label="Date and Duration"
                                        onClick={() => toggleDatePopover()}
                                        disabled={!form.values.instance_id}
                                        className="disabled:bg-transparent"
                                        size="sm"
                                    >
                                        <Icon
                                            icon={Pencil}
                                            color={form.values.instance_id ? 'text-black' : 'text-slate-400'}
                                        />
                                    </ActionIcon>
                                </Popover.Target>
                            </div>
                        </div>
                        <Popover.Dropdown className="flex flex-col gap-3">
                            <DateTimePickerInput
                                value={form.values.alert_time}
                                onChange={(value) => form.setFieldValue('alert_time', value)}
                                onClose={toggleDatePopover}
                            />
                        </Popover.Dropdown>
                    </Popover>
                    <FormSection>
                        <Select
                            label="Bearbeiter"
                            placeholder="Bearbeiter wählen"
                            data={assignee_options}
                            searchable
                            leftSection={<Search className="size-4" />}
                            value={form.values.employee_id}
                            onChange={(value) => handleEmployeeChange(form, value)}
                            disabled={!form.values.instance_id}
                        />
                        <CustomSearchableSelect
                            label="Patient"
                            search={search_patient}
                            setSearch={onSearchPatient}
                            setValue={(value) => handlePatientSelection(form, patient_options, value)}
                            options={patient_options}
                            leftSection={
                                is_loading_patient_search ? <Loader size="xs" /> : <Search className="size-4" />
                            }
                            disabled={!form.values.instance_id}
                            placeholder="Patient:in suchen"
                            notFoundMessage="Patient nicht gefunden"
                        />
                    </FormSection>
                </>
            )}
        </Form>
    );
};

export default TaskForm;
